import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import MainContainer from "../../components/MainContainer";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UserModal from "../../components/UserModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import toastError from "../../errors/toastError";
import {
  SocketContext,
  socketManager,
} from "../../context/Socket/SocketContext";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Avatar } from "@material-ui/core";
import ForbiddenPage from "../../components/ForbiddenPage";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { useHistory } from "react-router-dom";
import usePlans from "../../hooks/usePlans";
import { AEROS, stateText } from "../../styles/aerosTokens";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Grid da tabela — espelha o mockup Usuários A:
// ID | Status | Nome | E-mail | Perfil | Expediente | Ações
const TABLE_GRID = "60px 90px 2fr 2fr 1.1fr 1.6fr 1.2fr";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
  // ---- header da página (60px, título + contagem, ações à direita) ----
  pageHeader: {
    minHeight: 60,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 12px",
    borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
  },
  pageTitle: {
    fontFamily: AEROS.fontDisplay,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  pageTitleCount: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  headerActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  searchPill: {
    width: 230,
    backgroundColor: theme.palette.aeros.surface,
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
    borderRadius: 10,
    padding: "5px 14px",
    fontSize: 13,
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: 8,
    "& .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
    "& input::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  ghostButton: {
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"}`,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 17 },
  },
  gradientButton: {
    borderRadius: 10,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    "& .MuiSvgIcon-root": { fontSize: 17 },
  },
  // ---- corpo da página ----
  pageBody: {
    flex: 1,
    padding: "16px 12px",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  // ---- card da tabela em CSS grid ----
  tableCard: {
    backgroundColor: theme.palette.aeros.surface,
    border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
    borderRadius: 14,
    overflow: "hidden",
  },
  tableHeadRow: {
    display: "grid",
    gridTemplateColumns: TABLE_GRID,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.disabled || theme.palette.aeros.textTertiary,
    backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
    borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: TABLE_GRID,
    alignItems: "center",
    fontSize: 13.5,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
  },
  tableCell: {
    padding: "12px 8px",
    minWidth: 0,
  },
  bodyCell: {
    padding: "11px 8px",
    minWidth: 0,
  },
  cellFirst: {
    paddingLeft: 20,
  },
  cellActionsHead: {
    textAlign: "right",
    paddingRight: 20,
  },
  cellActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 6,
    paddingRight: 20,
  },
  idCell: {
    color: theme.palette.text.secondary,
  },
  nameCellInner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  nameLabel: {
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userAvatar: {
    width: 34,
    height: 34,
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  workCell: {
    color: theme.palette.text.secondary,
    fontSize: 12.5,
  },
  loadingRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    padding: "28px 0",
    color: theme.palette.text.secondary,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 600,
  },
  statusDot: {
    width: 6,
    height: 6,
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  statusPillOnline: {
    color: stateText(theme, theme.palette.success.main, "success"),
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  },
  statusPillOffline: {
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    border: `1px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
  },
  profileChip: {
    display: "inline-block",
    fontSize: 10.5,
    fontWeight: 600,
    borderRadius: 99,
    padding: "2px 10px",
  },
  profileChipAdmin: {
    backgroundColor: "rgba(160,68,255,0.12)",
    border: "1px solid rgba(160,68,255,0.35)",
    color: stateText(theme, "#C08BFF", "purple"),
  },
  profileChipUser: {
    backgroundColor: "transparent",
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
    color: theme.palette.text.secondary,
  },
  profileChipSuper: {
    backgroundColor: alpha(AEROS.cyan, 0.1),
    border: `1px solid ${alpha(AEROS.cyan, 0.35)}`,
    color: stateText(theme, AEROS.cyan, "info"),
  },
  emailCell: {
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowActionButton: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    padding: 0,
    "&:hover": {
      color: theme.palette.secondary.main,
      borderColor: alpha(AEROS.cyan, 0.4),
      backgroundColor: "transparent",
    },
    "& .MuiSvgIcon-root": { fontSize: 16 },
  },
  rowActionButtonDelete: {
    "&:hover": {
      color: theme.palette.error.main,
      borderColor: alpha(theme.palette.error.main, 0.4),
      backgroundColor: "transparent",
    },
  },
  };
});

const avatarColorFor = (id) => AEROS.avatarCycle[id % AEROS.avatarCycle.length];
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

const profileChipClassFor = (classes, profile) => {
  if (profile === "admin") return classes.profileChipAdmin;
  if (profile === "super") return classes.profileChipSuper;
  return classes.profileChipUser;
};

const Users = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmBackfillOpen, setConfirmBackfillOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;
  const companyId = loggedInUser.companyId;

  const [showInternalChat, setShowInternalChat] = useState(false);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      if (!companyId) {
        return;
      }

      try {
        setLoading(true);
        const planConfigs = await getPlanCompany(undefined, companyId);

        setShowInternalChat(planConfigs.plan.useInternalChat);
      } catch (err) {
        console.error("Error fetching plan:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

useEffect(() => {
  if (loggedInUser) {
    const companyId = loggedInUser.companyId;
    
    const onCompanyUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };
    
    socket.on(`company-${companyId}-user`, onCompanyUser);
    
    return () => {
      socket.off(`company-${companyId}-user`, onCompanyUser);
    };
  }
}, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

const renderProfileImage = (user) => {
  const buildImageUrl = (userData) => {
    if (!userData.profileImage) {
      return null;
    }
    return `${backendUrl}/public/company${userData.companyId}/user/${userData.profileImage}`;
  };

  const initialsStyle = {
    backgroundColor: avatarColorFor(user.id),
    color: "#07080D",
  };

  // Para o usuário logado, verificar localStorage também
  if (user.id === loggedInUser.id) {
    const savedProfileImage = localStorage.getItem("profileImage");
    const profileImageToUse = savedProfileImage || user.profileImage;

    const imageUrl = profileImageToUse
      ? `${backendUrl}/public/company${user.companyId}/user/${profileImageToUse}`
      : null;

    return (
      <Avatar
        src={imageUrl || undefined}
        alt={user.name}
        className={classes.userAvatar}
        style={imageUrl ? undefined : initialsStyle}
        onError={(e) => {
          e.target.src = whatsappIcon;
        }}
      >
        {!imageUrl && initialsOf(user.name)}
      </Avatar>
    );
  }

  const imageUrl = buildImageUrl(user);

  // Para outros usuários
  return (
    <Avatar
      src={imageUrl || undefined}
      alt={user.name}
      className={classes.userAvatar}
      style={imageUrl ? undefined : initialsStyle}
    >
      {!imageUrl && initialsOf(user.name)}
    </Avatar>
  );
};

  const handleCreateChat = async (targetUser) => {
    try {
      setLoading(true);
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      toast.success("Chat criado com sucesso!");
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillChats = async () => {
    setConfirmBackfillOpen(false);
    setLoading(true);
    try {
      await api.post("/chats/backfill");
      toast.success(i18n.t("users.chatsDone"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer>
      <AerosConfirmModal
        open={confirmModalOpen && Boolean(deletingUser)}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => {
          setConfirmModalOpen(false);
          handleDeleteUser(deletingUser.id);
        }}
        icon={<PersonRemoveOutlinedIcon />}
        tone="danger"
        title={i18n.t("users.confirmDelete.title", {
          name: deletingUser?.name || "",
        })}
        description={i18n.t("users.confirmDelete.text")}
        confirmLabel={i18n.t("users.confirmDelete.confirm")}
      />
      <AerosConfirmModal
        open={confirmBackfillOpen}
        onClose={() => setConfirmBackfillOpen(false)}
        onConfirm={handleBackfillChats}
        icon={<ForumOutlinedIcon />}
        tone="info"
        primary
        title={i18n.t("users.confirmChats.title")}
        description={i18n.t("users.confirmChats.text")}
        confirmLabel={i18n.t("users.confirmChats.confirm")}
      />
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <div className={classes.pageHeader}>
            <Typography component="div" className={classes.pageTitle}>
              {i18n.t("users.title")}{" "}
              <span className={classes.pageTitleCount}>· {users.length}</span>
            </Typography>
            <div className={classes.headerActions}>
              <InputBase
                className={classes.searchPill}
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                startAdornment={<SearchIcon />}
                inputProps={{ "aria-label": i18n.t("contacts.searchPlaceholder") }}
              />
              {loggedInUser.profile === "admin" && showInternalChat && (
                <Button
                  variant="outlined"
                  className={classes.ghostButton}
                  startIcon={<ForumOutlinedIcon />}
                  onClick={() => setConfirmBackfillOpen(true)}
                  disabled={loading}
                >
                  {i18n.t("users.confirmChats.confirm")}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                className={classes.gradientButton}
                startIcon={<PersonAddIcon />}
                onClick={handleOpenUserModal}
              >
                {i18n.t("users.buttons.add")}
              </Button>
            </div>
          </div>
          <div className={classes.pageBody} onScroll={handleScroll}>
            <div className={classes.tableCard}>
              <div className={classes.tableHeadRow}>
                <div className={`${classes.tableCell} ${classes.cellFirst}`}>
                  {i18n.t("users.table.ID")}
                </div>
                <div className={classes.tableCell}>
                  {i18n.t("users.table.status")}
                </div>
                <div className={classes.tableCell}>
                  {i18n.t("users.table.name")}
                </div>
                <div className={classes.tableCell}>
                  {i18n.t("users.table.email")}
                </div>
                <div className={classes.tableCell}>
                  {i18n.t("users.table.profile")}
                </div>
                <div className={classes.tableCell}>
                  {i18n.t("users.table.workHours")}
                </div>
                <div className={`${classes.tableCell} ${classes.cellActionsHead}`}>
                  {i18n.t("users.table.actions")}
                </div>
              </div>
              {users.map((user) => (
                <div className={classes.tableRow} key={user.id}>
                  <div
                    className={`${classes.bodyCell} ${classes.cellFirst} ${classes.idCell}`}
                  >
                    {user.id}
                  </div>
                  <div className={classes.bodyCell}>
                    <span
                      className={`${classes.statusPill} ${
                        user.online
                          ? classes.statusPillOnline
                          : classes.statusPillOffline
                      }`}
                    >
                      <span className={classes.statusDot} />
                      {user.online ? "Online" : "Offline"}
                    </span>
                  </div>
                  <div className={`${classes.bodyCell} ${classes.nameCellInner}`}>
                    {renderProfileImage(user)}
                    <span className={classes.nameLabel}>{user.name}</span>
                  </div>
                  <div
                    className={`${classes.bodyCell} ${classes.emailCell}`}
                    title={user.email}
                  >
                    {user.email}
                  </div>
                  <div className={classes.bodyCell}>
                    <span
                      className={`${classes.profileChip} ${profileChipClassFor(
                        classes,
                        user.profile
                      )}`}
                    >
                      {user.profile}
                    </span>
                  </div>
                  <div className={`${classes.bodyCell} ${classes.workCell}`}>
                    {user.startWork || user.endWork
                      ? `${user.startWork || "—"} – ${user.endWork || "—"}`
                      : "—"}
                  </div>
                  <div className={`${classes.bodyCell} ${classes.cellActions}`}>
                    <IconButton
                      size="small"
                      className={classes.rowActionButton}
                      onClick={() => handleCreateChat(user)}
                      disabled={!showInternalChat}
                      aria-label="Iniciar chat interno"
                    >
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={classes.rowActionButton}
                      onClick={() => handleEditUser(user)}
                      aria-label="Editar usuário"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={`${classes.rowActionButton} ${classes.rowActionButtonDelete}`}
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingUser(user);
                      }}
                      aria-label="Excluir usuário"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
              {(loadingMore || (loading && !loadingMore)) && (
                <div className={classes.loadingRow}>
                  <CircularProgress size={24} />
                  {loading && !loadingMore && (
                    <span>{i18n.t("loading")}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </MainContainer>
  );
};

export default Users;
